import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, CheckCircle, Info, XCircle } from 'lucide-react';
import Card from './Card';
import { CSS_CLASSES, CSS_STYLES, ANIMATIONS } from '../lib/styles';

interface AlertCardProps {
  type: 'warning' | 'success' | 'info' | 'error';
  title: string;
  message: string;
  icon?: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  onClick?: () => void;
  className?: string;
  animation?: {
    initial?: any;
    animate?: any;
    transition?: any;
    whileHover?: any;
  };
}

const AlertCard: React.FC<AlertCardProps> = ({
  type,
  title,
  message,
  icon: CustomIcon,
  onClick,
  className = '',
  animation = ANIMATIONS.FADE_IN
}) => {
  const getIcon = () => {
    if (CustomIcon) {
      return <CustomIcon className={CSS_CLASSES.ICON_SMALL} style={CSS_STYLES.TEXT_PRIMARY} />;
    }
    
    const iconProps = { className: CSS_CLASSES.ICON_SMALL, style: CSS_STYLES.TEXT_PRIMARY };
    
    switch (type) {
      case 'warning':
        return <AlertTriangle {...iconProps} />;
      case 'success':
        return <CheckCircle {...iconProps} />;
      case 'info':
        return <Info {...iconProps} />;
      case 'error':
        return <XCircle {...iconProps} />;
      default:
        return <AlertTriangle {...iconProps} />;
    }
  };

  const getTypeStyles = () => {
    switch (type) {
      case 'warning':
        return {
          borderLeft: '4px solid #f59e0b',
          backgroundColor: 'var(--bg-card)'
        };
      case 'success':
        return {
          borderLeft: '4px solid #22c55e',
          backgroundColor: 'var(--bg-card)'
        };
      case 'info':
        return {
          borderLeft: '4px solid #3b82f6',
          backgroundColor: 'var(--bg-card)'
        };
      case 'error':
        return {
          borderLeft: '4px solid #ef4444',
          backgroundColor: 'var(--bg-card)'
        };
      default:
        return CSS_STYLES.CARD;
    }
  };

  return (
    <Card 
      onClick={onClick} 
      animation={animation}
      className={`${CSS_CLASSES.CARD_BASE} ${onClick ? CSS_CLASSES.CARD_CLICKABLE : ''} ${className}`}
      hover={!!onClick}
    >
      <div style={getTypeStyles()}>
        <div className={CSS_CLASSES.FLEX_START}>
          <div className={CSS_CLASSES.ICON_CONTAINER} style={CSS_STYLES.CARD_TERTIARY}>
            {getIcon()}
          </div>
          <div className="flex-1">
            <p className={CSS_CLASSES.TEXT_PRIMARY} style={CSS_STYLES.TEXT_PRIMARY}>
              {title}
            </p>
            <p className={CSS_CLASSES.TEXT_SECONDARY} style={CSS_STYLES.TEXT_SECONDARY}>
              {message}
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default AlertCard;
